import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { DirectEditService } from '@app/modules/direct/services/direct-edit.service';
import {
  ConflictingDnsRecord,
  DomainsService,
  ListDomain,
  VerifyDnsResponse,
} from '@app/modules/domains/domains.service';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { DirectService, Domains } from '../../services/direct.service';

@Component({
  standalone: false,
  selector: 'sbnb-direct-edit-domains',
  templateUrl: './direct-edit-domains.component.html',
  styleUrls: ['./direct-edit-domains.component.scss'],
})
export class DirectEditDomainsComponent implements OnInit, OnDestroy {
  domains?: Domains;
  detailedDomain?: ListDomain;
  removingDomain = false;
  isVerifyingDns = false;
  conflictingDnsRecords: ConflictingDnsRecord[] = [];
  initialVerificationDone = false;
  dnsVerifiedSuccessfully = false;
  private destroy$ = new Subject<void>();

  constructor(
    private toast: ToastNotificationsService,
    private router: Router,
    public readonly directEditService: DirectEditService,
    private readonly direct: DirectService,
    private readonly domainsService: DomainsService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit() {
    this.dnsVerifiedSuccessfully = false;
    this.initialVerificationDone = false;
    this.directEditService.site$.pipe(takeUntil(this.destroy$)).subscribe((site) => {
      this.domains = site?.domains || ({} as Domains);
      this.loadAllDomains();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAllDomains(): void {
    this.domainsService
      .listDomains()
      .pipe(
        catchError((error) => {
          this.toast.error(error.message || 'Failed to load domains. Please try again.');
          return of([] as ListDomain[]);
        })
      )
      .subscribe((response) => {
        const currentSiteId = this.directEditService.site?.id;
        const foundDomain = response.find((domain) => domain.siteId === currentSiteId);
        this.detailedDomain = foundDomain;

        if (
          this.detailedDomain &&
          !this.detailedDomain.dnsManaged &&
          !this.initialVerificationDone
        ) {
          this.triggerVerification();
        }
        this.initialVerificationDone = true;
      });
  }

  reloadDomainDetails() {
    if (!this.detailedDomain) return;
    this.domainsService
      .getDomainByUuid(this.detailedDomain.uuid)
      .pipe(
        catchError((error) => {
          this.toast.error(error.message || 'Failed to load domain details. Please try again.');
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          this.detailedDomain.dnsManaged = response.dnsManaged;
          this.detailedDomain.nameserverVerifiedAt = response.nameserverVerifiedAt;
        }
      });
  }

  openAddDomainModal() {
    const siteId = this.directEditService.site?.id;
    if (siteId) {
      this.router.navigate(['/settings/domains/wizard', siteId]);
    } else {
      this.toast.open('Could not identify the site. Please reload and try again.', 'Dismiss', NotificationType.Error);
    }
  }

  confirmUnlinkCustomDomain(): void {
    if (!this.detailedDomain) {
      this.toast.error('Cannot find domain details to unlink.');
      return;
    }

    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '450px',
      data: {
        name: `domain ${this.detailedDomain.name}`,
        action: 'Unlink',
        additionalText: 'This will only unlink the domain from your site and revert back to your default subdomain. It will not delete the domain registration itself.',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.removeCustomDomain();
      }
    });
  }

  removeCustomDomain() {
    if (!this.directEditService.site?.id || !this.detailedDomain?.uuid) {
      this.toast.error('Could not identify the site or domain to remove. Please reload and try again.');
      return;
    }

    const domainUuidToRemove = this.detailedDomain.uuid;
    this.removingDomain = true;

    this.domainsService
      .unlinkDomainFromSite(this.detailedDomain.uuid)
      .pipe(
        finalize(() => (this.removingDomain = false)),
        catchError((error) => {
          this.toast.error(error?.message || 'Failed to remove custom domain. Please try again.');
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          this.toast.success('Custom domain removed successfully.');
          if (this.detailedDomain?.uuid === domainUuidToRemove) {
            this.detailedDomain = undefined;
            if (this.domains) {
              this.domains.custom_domain = undefined;
            }
          }
          this.loadAllDomains();
        }
      });
  }

  copiedToClipboard() {
    this.toast.open('Copied to clipboard', 'Dismiss', NotificationType.Success, 2500);
  }

  triggerVerification(): void {
    const siteId = this.directEditService.site?.id;
    const domainUuid = this.detailedDomain?.uuid;

    if (!siteId) {
      this.toast.error('Cannot identify the site. Please reload.');
      return;
    }
    if (!domainUuid) {
      this.toast.error('Cannot identify the domain to verify. Please reload.');
      return;
    }

    if (this.isVerifyingDns) {
      return;
    }

    this.dnsVerifiedSuccessfully = false;
    this.isVerifyingDns = true;
    this.conflictingDnsRecords = [];

    this.domainsService
      .verifyDomainDnsRecords(domainUuid)
      .pipe(
        finalize(() => {
          this.isVerifyingDns = false;
        }),
        catchError((err) => {
          const errorMsg = err?.error?.message || err?.message || 'An unexpected error occurred during DNS verification.';
          this.toast.error(errorMsg);
          return of({ success: false, message: errorMsg, conflictingRecords: [] } as VerifyDnsResponse);
        })
      )
      .subscribe((res: VerifyDnsResponse) => {
        if (res.success) {
          this.directEditService.getSite(siteId);
          this.dnsVerifiedSuccessfully = true;
        } else {
          const failureMsg =
            res.message || 'Domain verification failed. Please ensure your DNS records are set up correctly and try again shortly.';

          if (!res.conflictingRecords || res.conflictingRecords.length === 0) {
            this.toast.error(failureMsg);
          }

          if (res.conflictingRecords && res.conflictingRecords.length > 0) {
            this.conflictingDnsRecords = res.conflictingRecords;
          } else {
            this.conflictingDnsRecords = [];
          }
        }
      });
  }
}
